<template>
  <div class="unsubscribe-page">
    <div class="container">
      <div class="unsubscribe-card">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Wird verarbeitet...</p>
        </div>
        
        <div v-else-if="success" class="success-state">
          <div class="success-icon">✓</div>
          <h1>Abmeldung erfolgreich</h1>
          <p>Du erhältst keine {{ getNotificationTypeLabel(notificationType) }}-Benachrichtigungen mehr per E-Mail.</p>
          <p>Falls du deine Benachrichtigungseinstellungen ändern möchtest, kannst du dich in deinem Quitly-Konto anmelden.</p>
          <div class="actions">
            <router-link to="/" class="btn btn-primary">Zur Quitly-Startseite</router-link>
            <router-link to="/auth/login" class="btn btn-outline-primary">Anmelden</router-link>
          </div>
        </div>
        
        <div v-else-if="error" class="error-state">
          <div class="error-icon">⚠</div>
          <h1>Fehler</h1>
          <p>{{ errorMessage }}</p>
          <div class="actions">
            <router-link to="/" class="btn btn-primary">Zur Quitly-Startseite</router-link>
          </div>
        </div>
      </div>
      
      <div class="footer-info">
        <p>Diese E-Mail wurde von Quitly gesendet, da du Benachrichtigungen aktiviert hattest.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import axios from '@/api/axios'

export default {
  name: 'UnsubscribeView',
  setup() {
    const route = useRoute()
    const loading = ref(true)
    const success = ref(false)
    const error = ref(false)
    const errorMessage = ref('')
    const notificationType = ref('')

    const getNotificationTypeLabel = (type) => {
      const labels = {
        'teamInvitation': 'Team-Einladung',
        'teamRequest': 'Teamanfrage',
        'teamRequestResponse': 'Teamanfrage-Antwort',
        'teamReceiptCreated': 'Team-Beleg erstellt',
        'teamReceiptUpdated': 'Team-Beleg aktualisiert',
        'teamReceiptDeleted': 'Team-Beleg gelöscht',
        'teamMemberJoined': 'Team-Mitglied beigetreten',
        'teamMemberLeft': 'Team-Mitglied verlassen',
        'teamInvitationAccepted': 'Team-Einladung angenommen',
        'teamInvitationDeclined': 'Team-Einladung abgelehnt',
        'teamRequestAccepted': 'Teamanfrage angenommen',
        'teamRequestDeclined': 'Teamanfrage abgelehnt'
      }
      return labels[type] || type
    }

    const processUnsubscribe = async () => {
      try {
        const token = route.params.token
        if (!token) {
          throw new Error('Ungültiger Unsubscribe-Link')
        }

        const response = await axios.get(`/unsubscribe/${token}`)
        
        if (response.data.success) {
          success.value = true
          notificationType.value = response.data.notificationType
        } else {
          throw new Error(response.data.message || 'Unbekannter Fehler')
        }
      } catch (err) {
        error.value = true
        errorMessage.value = err.response?.data?.message || err.message || 'Ein Fehler ist aufgetreten'
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      processUnsubscribe()
    })

    return {
      loading,
      success,
      error,
      errorMessage,
      notificationType,
      getNotificationTypeLabel
    }
  }
}
</script>

<style scoped>
.unsubscribe-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.container {
  max-width: 500px;
  width: 100%;
}

.unsubscribe-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 40px;
  text-align: center;
}

.loading-state {
  padding: 40px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4a86e8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-icon {
  color: #28a745;
  font-size: 4rem;
  margin-bottom: 20px;
}

.error-icon {
  color: #dc3545;
  font-size: 4rem;
  margin-bottom: 20px;
}

h1 {
  color: #333;
  margin-bottom: 20px;
  font-weight: 600;
}

p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
}

.actions {
  margin-top: 30px;
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-block;
  padding: 12px 24px;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.btn-primary {
  background: #4a86e8;
  color: white;
}

.btn-primary:hover {
  background: #357abd;
  transform: translateY(-1px);
}

.btn-outline-primary {
  background: transparent;
  color: #4a86e8;
  border-color: #4a86e8;
}

.btn-outline-primary:hover {
  background: #4a86e8;
  color: white;
  transform: translateY(-1px);
}

.footer-info {
  margin-top: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  text-align: center;
}

.footer-info p {
  margin: 0;
  font-size: 0.9rem;
  color: #999;
}

@media (max-width: 576px) {
  .unsubscribe-card {
    padding: 30px 20px;
  }
  
  .actions {
    flex-direction: column;
    align-items: center;
  }
  
  .btn {
    width: 100%;
    max-width: 200px;
  }
}
</style>
